/**
 * Os mapas do exame no painel de localização (E90.F3, ADR-0081).
 *
 * A imagem vem de rota autenticada, então não vai direto no `src`: cada mapa
 * busca os bytes e monta uma URL local, revogada quando o mapa (ou o caso) muda.
 * A chave do efeito é a consulta: trocar de caso troca a consulta, e a resposta
 * tardia do caso anterior é descartada pela flag de cancelamento.
 *
 * Mapa não gerado mostra o motivo; nunca uma imagem de outro mapa.
 */
import { useEffect, useState } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { baixarMapaDoExame, type MapaDoExameData } from '@/lib/exame_eletronico_localizacao';

import { formatarDistancia } from './QuadroVerificacaoCluster';
import { Titulo } from './QuadroVerificacaoTabelas';

const t = ptBR.exameEletronico.quadro;

type EstadoDaImagem = { tipo: 'carregando' } | { tipo: 'pronta'; url: string } | { tipo: 'falhou' };

function useImagemDoMapa(caseId: string, consultaId: string): EstadoDaImagem {
  const client = useApiClient();
  const [estado, setEstado] = useState<EstadoDaImagem>({ tipo: 'carregando' });

  useEffect(() => {
    let cancelado = false;
    let criada: string | null = null;
    void (async () => {
      try {
        const blob = await baixarMapaDoExame(client, caseId, consultaId);
        if (cancelado) return;
        criada = URL.createObjectURL(blob);
        setEstado({ tipo: 'pronta', url: criada });
      } catch {
        if (!cancelado) setEstado({ tipo: 'falhou' });
      }
    })();
    return () => {
      cancelado = true;
      if (criada !== null) URL.revokeObjectURL(criada);
    };
  }, [client, caseId, consultaId]);

  return estado;
}

function ImagemDoMapa({
  caseId,
  mapa,
  consultaId,
}: {
  caseId: string;
  mapa: MapaDoExameData;
  consultaId: string;
}): JSX.Element {
  const estado = useImagemDoMapa(caseId, consultaId);
  if (estado.tipo === 'carregando') {
    return <p className="text-caption text-fg-dim">{t.mapaCarregando}</p>;
  }
  if (estado.tipo === 'falhou') {
    return (
      <p className="text-caption text-fg-dim" data-testid={`quadro-mapa-${mapa.tipo}-falhou`}>
        {t.mapaFalhou}
      </p>
    );
  }
  return (
    <img
      src={estado.url}
      alt={t.mapaAlt[mapa.tipo]}
      className="w-full max-w-[48rem] border border-border"
      data-testid={`quadro-mapa-${mapa.tipo}-imagem`}
    />
  );
}

function UmMapa({ caseId, mapa }: { caseId: string; mapa: MapaDoExameData }): JSX.Element {
  const consultaId = mapa.situacao === 'confere' ? mapa.consulta_id : null;
  return (
    <figure className="space-y-sc-2" data-testid={`quadro-mapa-${mapa.tipo}`}>
      <figcaption className="text-body-sm font-medium text-fg">
        {t.mapaTitulo[mapa.tipo]}
      </figcaption>
      {consultaId ? (
        <>
          {/* A `key` recomeça o estado da imagem quando o caso ou a consulta mudam:
              sem ela, a URL já revogada do mapa anterior apareceria por um quadro. */}
          <ImagemDoMapa
            key={`${caseId}:${consultaId}`}
            caseId={caseId}
            mapa={mapa}
            consultaId={consultaId}
          />
          <ul className="space-y-sc-1 text-caption text-fg-muted">
            {mapa.marcadores.map((m) => (
              <li key={m.letra}>
                <span className="font-mono text-fg">{m.letra}</span> — {m.referencia}
              </li>
            ))}
            {mapa.raio_m ? (
              <li>{t.mapaRaio[mapa.tipo].replace('{raio}', formatarDistancia(mapa.raio_m))}</li>
            ) : null}
          </ul>
        </>
      ) : (
        <p className="text-caption text-fg-dim" data-testid={`quadro-mapa-${mapa.tipo}-nao-gerado`}>
          {t.mapaNaoGerado.replace('{motivo}', mapa.detalhe ?? '—')}
        </p>
      )}
    </figure>
  );
}

export function MapasDoExame({
  caseId,
  mapas,
}: {
  caseId: string;
  mapas: MapaDoExameData[] | null | undefined;
}): JSX.Element {
  const algumGerado = (mapas ?? []).some((m) => m.situacao === 'confere');
  return (
    <section className="space-y-sc-4" data-testid="quadro-mapas">
      <Titulo>{t.mapas}</Titulo>
      {!mapas || mapas.length === 0 ? (
        <p className="text-body-sm text-fg-dim" data-testid="quadro-mapas-nao-constam">
          {t.mapasNaoConstam}
        </p>
      ) : (
        mapas.map((mapa) => <UmMapa key={mapa.tipo} caseId={caseId} mapa={mapa} />)
      )}
      {algumGerado ? <p className="text-caption text-fg-dim">{t.mapaAtribuicao}</p> : null}
    </section>
  );
}
